<template>
    <button
    class="button-secondary"
    :disabled="disabled"
    :class="[
        { 'icon-only': !label || buttonStyle === 'round' },
        { 'selected': selected },
        { 'disabled': disabled },
        { 'error': hasError },
        buttonStyle,
        buttonSize
    ]"
    @click.stop="emit('clicked')">
        <!-- Badge -->
        <div v-if="badgeCounter > 0 && buttonStyle === 'round'" class="badge">{{ badgeCounterValue }}</div>
        <!-- Button Icon -->
        <app-icon v-if="icon" class="icon"/>
        <!-- Button label -->
        <div v-if="label && buttonStyle !== 'round'" class="label">{{ label }}</div>
    </button>
</template>

<script setup>
/* Vue */
import { computed, defineAsyncComponent } from 'vue'
import { iconModules } from '@/assets/icons/iconModules'
/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received:
@label {String}
@icon {String}
@buttonStyle {String} can be [square (default), round]
@buttonSize {String} can be [medium (default), small, tiny]
@selected {Boolean}
@disabled {Boolean}
@hasError {Boolean}
@badgeCounter {Number} number to display on the badge
*/
const props = defineProps({
    label: String,
	icon: String,
	buttonStyle: {
        type: String,
        default: 'square'
    },
    buttonSize: {
        type: String,
        default: 'medium'
    },
    selected: Boolean,
    disabled: Boolean,
    hasError: Boolean,
    badgeCounter: Number
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})

const badgeCounterValue = computed(() => {
    return props.badgeCounter > 9 ? '9+' : props.badgeCounter
})
</script>

<style lang="scss" scoped>
.button-secondary {
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    gap: 8px;

    padding: 0 16px;

    @include H7-Medium;
    color: var(--secondary-dark-2);

    background-color: var(--secondary-light-2);

    cursor: pointer;

    .badge {
        position: absolute;
        top: 0;
        right: 0;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 16px;
        height: 16px;

        border-radius: 20px;

        @include H9-Regular;
        color: var(--secondary-light-4);

        background-color: var(--negative);
    }

    svg.icon {
        flex-shrink: 0;

        width: 16px;
        height: 16px;
        pointer-events: none;

        :deep(.change-color-fill) {
            fill: var(--primary);
        }
    }

    /* Style Classes */
    &.square {
        border-radius: 8px;
    }

    &.round {
        border-radius: 40px;
    }

    /* Size Classes */
    &.medium {
        height: 48px;

        &.icon-only {
            width: 48px;
        }
    }

    &.small {
        height: 40px;

        &.icon-only {
            width: 40px;
        }
    }

    &.tiny {
        height: 32px;

        padding: 0 8px;

        &.icon-only {
            width: 32px;
        }
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: var(--button-secondary-state-hover);
        }
    }

    &:active {
        background-color: var(--button-secondary-state-press);
    }

    &.error,
    &.selected {
        background-color: var(--secondary-light-3);
    }

    &.selected {
        border: 2px solid var(--primary);

        color: var(--primary);
    }

    &.error {
        border: 1px solid var(--negative);
    }

    &:disabled {
        cursor: default;
        pointer-events: none;
        opacity: 0.24;
    }
}
</style>
